import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/auth';
import { subscriptionService } from '@/services/subscription/subscription.service';
import { useAuthStore } from '@/store/use-auth-store';
import { companyMembershipService } from '@/services/company-membership/company-membership.service';
import { toast } from 'sonner';
import type { OnboardingStatus, User } from '@/types/user.types';

const GoogleSuccessPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, setRoles } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const maxRetries = 5;
  const minLoadingTime = 800; // Minimum time to show loading to prevent flash

  useEffect(() => {
    const handleGoogleAuthSuccess = async () => {
      const startTime = Date.now();
      
      try {
        setIsLoading(true);

        // Get user profile to authenticate
        const profileResponse = await authService.getProfile() as { roles: string[] } & User;
        const { roles, ...user } = profileResponse;

        if (user.status !== "ENABLED") {
          toast.error(t("common.accountDisabled"));
          navigate("/auth/login");
          return;
        }

        setUser(user);
        setRoles(roles);

        // Check onboarding status (profile completion) with retry logic
        let onboardingStatus: OnboardingStatus | null = null;
        let lastError: unknown = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            onboardingStatus = await authService.getOnboardingStatus();
            console.log(`[Google Success] Onboarding status retrieved on attempt ${attempt + 1}:`, onboardingStatus);
            break; // Success, exit retry loop
          } catch (error) {
            lastError = error;
            console.warn(`[Google Success] Onboarding status check failed on attempt ${attempt + 1}:`, error);
            if (attempt < maxRetries) {
              // Wait before retrying (exponential backoff: 1s, 2s, 3s, 4s, 5s)
              const delay = 1000 * (attempt + 1);
              console.log(`[Google Success] Retrying in ${delay}ms...`);
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }
        }

        // Ensure minimum loading time to prevent flash
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < minLoadingTime) {
          await new Promise((resolve) => setTimeout(resolve, minLoadingTime - elapsedTime));
        }

        if (!onboardingStatus) {
          console.error("[Google Success] Failed to get onboarding status after all retries:", lastError);
          // Fallback: redirect to onboarding for profile completion
          navigate("/onboarding", { replace: true });
          return;
        }

        console.log("[Google Success] Final onboarding status:", onboardingStatus);

        // Trust the backend's verdict: if onboarding is fully complete
        // (e.g. admins, invited users, users with an active company) go
        // straight to the dashboard. Avoids re-deriving company status from
        // raw memberships, which sent admins (no membership) to onboarding.
        if (onboardingStatus.currentStep === 5 || onboardingStatus.companyComplete) {
          console.log("[Google Success] Onboarding complete, redirecting to dashboard");
          navigate("/", { replace: true });
          return;
        }

        // Check profile completion first - even invited users need to complete profile
        if (!onboardingStatus.profileComplete) {
          console.log("[Google Success] Profile incomplete, redirecting to onboarding");
          navigate("/onboarding", { replace: true });
          return;
        }

        // Profile is complete - check if user has companies or was invited
        let memberships: any[] = [];
        try {
          memberships = await companyMembershipService.getUserMemberships();
          console.log(`[Google Success] User memberships:`, memberships.length);
          
          if (memberships.length > 0 || onboardingStatus.wasInvited) {
            console.log("[Google Success] User has companies or was invited, redirecting to dashboard");
            navigate("/", { replace: true });
          } else {
            console.log("[Google Success] Profile complete but no companies, redirecting to create-company");
            navigate("/create-company", { replace: true });
          }
        } catch (membershipError) {
          console.warn("[Google Success] Failed to check memberships:", membershipError);
          // Fallback: if profile is complete and user was invited, go to home
          if (onboardingStatus.wasInvited) {
            navigate("/", { replace: true });
          } else {
            navigate("/create-company", { replace: true });
          }
        }
      } catch (error) {
        console.error("[Google Success] Failed to handle Google auth success:", error);
        toast.error(t("common.operationFailed"));
        navigate("/auth/login", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    handleGoogleAuthSuccess();
  }, [navigate, setUser, setRoles, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{t("common.loading") || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return null;
};

export default GoogleSuccessPage;

